import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import './ShippingLocationManager.css';
import './GlobalStyles.css';

const ShippingLocationManager = () => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [locations, setLocations] = useState([]);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationCode, setNewLocationCode] = useState('');
  const [selectedLocationToDelete, setSelectedLocationToDelete] = useState(null);
  const [usageEntries, setUsageEntries] = useState([]); // Shipping entries using the location
  const [reassignments, setReassignments] = useState({}); // Mapping: shippingID => new location
  const [loading, setLoading] = useState(true);

  // Fetch available shipping locations on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/locations`);
        // Each location object is assumed to have LocationName and LocationCode
        const options = response.data.map(loc => ({
          value: loc.LocationName.trim(),
          label: `${loc.LocationName.trim()} (${loc.LocationCode.trim()})`
        }));
        setLocations(options);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, [apiBaseUrl]);

  // Handle adding a new location (requires both name and abbreviation)
  const handleAddLocation = async () => {
    const trimmedName = newLocationName.trim();
    const trimmedCode = newLocationCode.trim();
    if (!trimmedName || !trimmedCode) {
      alert('Please enter both a location name and an abbreviation.');
      return;
    }
    if (trimmedCode.length > 4) {
      alert('Location abbreviation must be 4 characters or less.');
      return;
    }
    try {
      await axios.post(`${apiBaseUrl}/api/locations`, { 
        LocationName: trimmedName, 
        LocationCode: trimmedCode 
      });
      const newOption = { 
        value: trimmedName, 
        label: `${trimmedName} (${trimmedCode})` 
      };
      setLocations(prev => [...prev, newOption]);
      setNewLocationName('');
      setNewLocationCode('');
      alert(`New location "${trimmedName}" added successfully.`);
    } catch (error) {
      console.error('Error adding new location:', error);
      if (error.response && error.response.data && error.response.data.error) {
        alert(error.response.data.error);
      } else {
        alert('Error adding new location.');
      }
    }
  };

  // Handle clicking the Delete button for a location
  const handleDeleteLocationClick = async (locationOption) => {
    setSelectedLocationToDelete(locationOption);
    try {
      // Check if any shipping entries are using this location.
      // (Assumed endpoint: GET /api/location-usage/{location})
      const response = await axios.get(
        `${apiBaseUrl}/api/location-usage/${encodeURIComponent(locationOption.value)}`
      );
      const usage = response.data; // Assume an array of shipping entry objects
      setUsageEntries(usage);
      if (usage.length === 0) {
        // No shipping entries: confirm deletion immediately.
        if (window.confirm(`Are you sure you want to delete the location "${locationOption.value}"?`)) {
          await deleteLocation(locationOption.value);
        }
      }
    } catch (error) {
      console.error('Error checking location usage:', error);
      alert('Error checking location usage.');
    }
  };

  // Delete the location (after any necessary reassignment)
  const deleteLocation = async (locationName) => {
    try {
      await axios.delete(`${apiBaseUrl}/api/locations/${encodeURIComponent(locationName)}`);
      setLocations(prev => prev.filter(loc => loc.value !== locationName));
      alert(`Location "${locationName}" deleted successfully.`);
      // Clear deletion state
      setSelectedLocationToDelete(null);
      setUsageEntries([]);
      setReassignments({});
    } catch (error) {
      console.error('Error deleting location:', error);
      alert('Error deleting location.');
    }
  };

  // Handle reassigning a shipping entry to a new location
  const handleReassignmentChange = (shippingID, selectedOption) => {
    setReassignments(prev => ({ ...prev, [shippingID]: selectedOption.value }));
  };

  // Save reassignments for all shipping entries using the location and then delete the location.
  const handleSaveReassignments = async () => {
    // Ensure each shipping entry has a new location selected
    for (const entry of usageEntries) {
      if (!reassignments[entry.ShippingID]) {
        alert(`Please reassign shipping entry ${entry.ShippingID} to a new location.`);
        return;
      }
    }
    try {
      // For each shipping entry, update its location.
      // Note: We're now sending oldLocation along with shippingID and newLocation.
      for (const entry of usageEntries) {
        await axios.put(`${apiBaseUrl}/api/shipping-entry/reassign`, {
          shippingID: entry.ShippingID,
          oldLocation: selectedLocationToDelete.value,
          newLocation: reassignments[entry.ShippingID],
        });
      }
      // Now that all entries have been reassigned, delete the location.
      await deleteLocation(selectedLocationToDelete.value);
      alert('Shipping entries reassigned and location deleted successfully.');
    } catch (error) {
      console.error('Error reassigning shipping entries:', error);
      alert('Error reassigning shipping entries.');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-spinner">Loading shipping locations...</div>
      </div>
    );
  }

  return (
    <div className="page-container shipping-location-manager">
      <div className="header-container">
        <h2>Shipping Location Manager</h2>
      </div>

      {/* Section: Add New Location */}
      <div className="admin-tools-container" style={{ marginBottom: '20px' }}>
        <h3>Add New Location</h3>
        <div className="universal-form">
          <div className="universal-form-group">
            <label htmlFor="new-location-name">Location Name</label>
            <input
              id="new-location-name"
              type="text"
              value={newLocationName}
              onChange={(e) => setNewLocationName(e.target.value)}
              placeholder="Enter new location name"
              className="admin-input"
            />
          </div>
          <div className="universal-form-group">
            <label htmlFor="new-location-code">Abbreviation</label>
            <input
              id="new-location-code"
              type="text"
              value={newLocationCode}
              onChange={(e) => setNewLocationCode(e.target.value)}
              placeholder="Abbreviation (max 4 chars)"
              className="admin-input"
              maxLength="4"
            />
          </div>
          <button onClick={handleAddLocation} className="create-location-button">
            Add Location
          </button>
        </div>
      </div>

      {/* Section: Available Locations */}
      <div className="admin-tools-container" style={{ marginBottom: '20px' }}>
        <h3>Available Locations</h3>
        {locations.length === 0 ? (
          <p>No locations available.</p>
        ) : (
          <ul className="location-list">
            {locations.map((loc, idx) => (
              <li key={idx} className="universal-form-group">
                <span>{loc.label}</span>
                <button
                  onClick={() => handleDeleteLocationClick(loc)}
                  className="global-delete-button"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Section: Reassignment UI if deletion is blocked */}
      {selectedLocationToDelete && usageEntries.length > 0 && (
        <div className="admin-tools-container">
          <h3>
            Shipping Entries Using "{selectedLocationToDelete.value}" – Reassign Before Deletion
          </h3>
          <table className="admin-log-table">
            <thead>
              <tr>
                <th>Shipping ID</th>
                <th>Current Location</th>
                <th>New Location</th>
              </tr>
            </thead>
            <tbody>
              {usageEntries.map((entry, index) => (
                <tr key={index}>
                  <td>{entry.ShippingID}</td>
                  <td>{selectedLocationToDelete.value}</td>
                  <td>
                    <Select
                      options={locations.filter(
                        (loc) => loc.value !== selectedLocationToDelete.value
                      )}
                      onChange={(selectedOption) =>
                        handleReassignmentChange(entry.ShippingID, selectedOption)
                      }
                      placeholder="Select new location"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={handleSaveReassignments} className="global-save-button">
            Save Reassignments &amp; Delete Location
          </button>
        </div>
      )}
    </div>
  );
};

export default ShippingLocationManager;
